/**
 * Keep the home-screen app on the newest console build. The service worker caches nothing, but a PWA that is
 * resumed from the home screen keeps the page it loaded days ago. On load, whenever the app comes back to the
 * foreground, and every ten minutes, the current index.html is fetched (no cache) and its build id compared with
 * the one this page was built with; a different id means a newer deploy: caches and local state are cleared and
 * the page reloads. The sign-in cookie is kept on purpose, so an update never logs anyone out.
 */
declare const __GLYPH_BUILD__: string;

const GUARD = 'glyph-build-reload-at';
const KEEP = new Set(['glyph-theme', 'glyph-last-device', GUARD]);
const EVERY_MS = 10 * 60 * 1000;
let checking = false;

export const CONSOLE_BUILD = typeof __GLYPH_BUILD__ === 'string' ? __GLYPH_BUILD__ : 'dev';

async function latestBuild(): Promise<string | null> {
  try {
    const r = await fetch(`/index.html?b=${Date.now()}`, { cache: 'no-store', credentials: 'same-origin' });
    if (!r.ok) return null;
    const m = /name="glyph-build"\s+content="([^"]+)"/.exec(await r.text());
    return m?.[1] ?? null;
  } catch {
    return null;
  }
}

async function clearLocalState() {
  try {
    if ('caches' in window) for (const k of await caches.keys()) await caches.delete(k);
  } catch {
    /* no cache storage */
  }
  try {
    for (const k of Object.keys(localStorage)) if (!KEEP.has(k)) localStorage.removeItem(k);
    sessionStorage.clear();
  } catch {
    /* storage blocked */
  }
}

export async function checkForNewBuild(): Promise<boolean> {
  if (checking || CONSOLE_BUILD === 'dev') return false;
  checking = true;
  try {
    const latest = await latestBuild();
    if (!latest || latest === CONSOLE_BUILD) return false;
    // One reload a minute at most: if the served page and the bundle ever disagree, the app must not spin.
    try {
      const last = Number(localStorage.getItem(GUARD) ?? 0);
      if (Date.now() - last < 60_000) return false;
      localStorage.setItem(GUARD, String(Date.now()));
    } catch {
      /* storage blocked */
    }
    await clearLocalState();
    if ('serviceWorker' in navigator) {
      const reg = await navigator.serviceWorker.getRegistration();
      await reg?.update().catch(() => undefined);
    }
    location.reload();
    return true;
  } finally {
    checking = false;
  }
}

export function startBuildWatch() {
  if (CONSOLE_BUILD === 'dev') return;
  void checkForNewBuild();
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void checkForNewBuild();
  });
  window.addEventListener('focus', () => void checkForNewBuild());
  setInterval(() => void checkForNewBuild(), EVERY_MS);
}
